<script setup lang="ts">
import { ESumSubAction } from '~/interfaces/dto/compliance/request';
import {
  EIdNumberType,
  type TIdNumberType,
} from '~/interfaces/stores/auth.store.interface';
import { globalKeys } from 'assets/data/global-keys';

const { $trackEvent } = useNuxtApp();
const { $authService, $storageService } = useServices();
const auth = useAuthStore();

const unauthenticatedToken = ref<string>();
const errorCode = ref<string>(null);
const idNum = ref<string>('');
const idNumType = ref<EIdNumberType>(EIdNumberType.IdNumber);
const idEntered = ref<boolean>(false);
const authorised = ref<boolean>(false);
const loading = ref<boolean>(false);

const initForgotLogin = async ({
  idNumber,
  idNumberType,
}: {
  idNumber: string;
  idNumberType: TIdNumberType;
}) => {
  idNum.value = idNumber;
  idNumType.value =
    EIdNumberType[
      (idNumberType.slice(0, 1).toUpperCase() +
        idNumberType.slice(1)) as keyof typeof EIdNumberType
    ];
  const initResponse = await $authService.forgotLoginInit({
    idNumber: idNum.value,
    idType: idNumType.value,
  });
  if (initResponse.isSuccessful) {
    errorCode.value = null;
    unauthenticatedToken.value = initResponse.data.token;
    idEntered.value = true;
    $storageService.setSessionStorage({
      key: globalKeys.authKeys.sumSubLastLivenessToken,
      data: initResponse.data.token,
    });
    $trackEvent('Account', 'MY_ACCOUNT_SUM_SUB_ACTION', {
      action: ESumSubAction[ESumSubAction.ForgotLogin],
      process: 'forgot-login-initialise',
    });
  } else {
    errorCode.value = initResponse?.error?.code || 'forgot-login-general-error';
  }
};

const authoriseForgotLogin = async () => {
  loading.value = true;
  const authResponse = await $authService.forgotLoginAuthorise({
    idNumber: idNum.value,
    idType: idNumType.value,
    token: unauthenticatedToken.value,
  });
  if (authResponse.isSuccessful) {
    auth.setAccessToken(authResponse.data);
    const userExtended = await $authService.getUserExtended();
    if (!!userExtended) {
      authorised.value = true;
      loading.value = false;
      $trackEvent('Account', 'MY_ACCOUNT_SUM_SUB_ACTION', {
        action: ESumSubAction[ESumSubAction.ForgotLogin],
        process: 'forgot-login-authorise',
      });
    } else {
      restart();
      errorCode.value = 'forgot-login-general-error';
    }
  }
};

const restart = () => {
  idEntered.value = false;
  errorCode.value = null;
  idNum.value = '';
  idNumType.value = EIdNumberType.IdNumber;
  authorised.value = false;
};

onMounted(() => {
  if (!!auth.currentAccessToken) authorised.value = true;
});
</script>

<template>
  <div class="bg-base w-full">
    <UserSumsub
      v-if="!authorised"
      v-model="idEntered"
      display-error
      external-evaluation
      :pregenerated-token="unauthenticatedToken"
      :action="ESumSubAction.ForgotLogin"
      @complete="authoriseForgotLogin"
    >
      <UserProfileManagementConfirmIdentification
        v-if="!idEntered && !loading"
        mode="logged-out"
        @id-entered="initForgotLogin($event)"
      >
        <template #error>
          <UserInterfaceGenericError
            v-if="errorCode"
            :error-code="errorCode"
            class="mb-4"
          >
            <span>{{ $t(`password-reset-logged-out-${errorCode}`) }}</span>
          </UserInterfaceGenericError>
        </template>
      </UserProfileManagementConfirmIdentification>
    </UserSumsub>
    <UserProfileManagementForgotLoginOptions
      v-if="authorised && !loading"
      :number="auth.getMobileNumber"
      @restart="restart"
    />
    <UserInterfaceGenericLoader v-if="loading" />
  </div>
</template>
